import { collection, query, where, orderBy, limit, getDocs, updateDoc, doc, startAfter, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';

const NOTIFICATIONS_PER_PAGE = 10;

const handleFirestoreError = (error) => {
    if (error.message.includes('requires an index')) {
        return { notifications: [], lastDoc: null, hasMore: false };
    }
    throw error;
};

// Helper function to build notification query
const buildNotificationsQuery = (employeeId, startAfterDoc = null) => {
    const baseQuery = [
        collection(db, 'notifications'),
        where('eid', '==', employeeId),
        orderBy('createdAt', 'desc'),
        limit(NOTIFICATIONS_PER_PAGE)
    ];

    if (startAfterDoc) {
        return query(...baseQuery, startAfter(startAfterDoc));
    }

    return query(...baseQuery);
};

export const fetchNotifications = async (employeeId, lastDoc = null) => {
    try {
        let notificationsQuery;

        if (lastDoc) {
            // For pagination, we need to reconstruct the document reference
            try {
                // Get the last document by ID to ensure we have a valid reference
                const lastDocRef = doc(db, 'notifications', lastDoc.id);
                const lastDocSnap = await getDoc(lastDocRef);

                if (lastDocSnap.exists()) {
                    notificationsQuery = buildNotificationsQuery(employeeId, lastDocSnap);
                } else {
                    notificationsQuery = buildNotificationsQuery(employeeId);
                }
            } catch (error) {
                // Fallback to initial query
                notificationsQuery = buildNotificationsQuery(employeeId);
            }
        } else {
            notificationsQuery = buildNotificationsQuery(employeeId);
        }

        const snapshot = await getDocs(notificationsQuery);

        const notifications = [];
        const seenIds = new Set(); // Track seen notification IDs

        snapshot.forEach((doc) => {
            // Skip if we've already seen this notification
            if (seenIds.has(doc.id)) return;

            seenIds.add(doc.id);
            const data = doc.data();

            // Convert Firestore timestamp to ISO string for Redux serialization
            const createdAtTimestamp = data.createdAt;
            const createdAt = createdAtTimestamp && typeof createdAtTimestamp.toDate === 'function'
                ? createdAtTimestamp.toDate().toISOString()
                : new Date().toISOString();

            notifications.push({
                id: doc.id,
                message: data.notification?.message?.body || data.notification?.title || 'Notification',
                type: data.type || 'SHIFT_UPDATE',
                isRead: data.status === 'read',
                createdAt: createdAt,
                data: data.notification?.message?.data || {}
            });
        });

        return {
            notifications,
            lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
            hasMore: snapshot.docs.length === NOTIFICATIONS_PER_PAGE
        };
    } catch (error) {
        return handleFirestoreError(error);
    }
};

export const markNotificationAsRead = async (notificationId) => {
    try {
        const notificationRef = doc(db, 'notifications', notificationId);
        await updateDoc(notificationRef, {
            status: 'read',
            readAt: new Date()
        });
        return notificationId;
    } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
    }
};

export const markAllNotificationsAsRead = async (employeeId) => {
    try {
        const batch = writeBatch(db);
        const unreadQuery = query(
            collection(db, 'notifications'),
            where('eid', '==', employeeId),
            where('status', '==', 'unread')
        );

        const snapshot = await getDocs(unreadQuery);

        if (snapshot.empty) {
            return { updated: 0 };
        }

        snapshot.forEach((doc) => {
            batch.update(doc.ref, {
                status: 'read',
                readAt: new Date()
            });
        });

        await batch.commit();
        return { updated: snapshot.size };
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
    }
};

export const getUnreadCount = async (employeeId) => {
    try {
        const unreadQuery = query(
            collection(db, 'notifications'),
            where('eid', '==', employeeId),
            where('status', '==', 'unread')
        );
        const snapshot = await getDocs(unreadQuery);
        return snapshot.size;
    } catch (error) {
        console.error('Error getting unread count:', error);
        return 0; // Return 0 if there's an error, including missing index
    }
};